/**
 * Messages.jsx
 * src/pages/assistant/Messages.jsx
 */

import { useState, useEffect, useRef } from "react";
import { Avatar, PageHeader, Loading, EmptyState, apiFetch } from "../../components/helpers";


export default function Messages() {
  const [conversations, setConversations] = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [messages,      setMessages]      = useState([]);
  const [search,        setSearch]        = useState("");
  const [newMsg,        setNewMsg]        = useState("");
  const [loading,       setLoading]       = useState(true);
  const [sending,       setSending]       = useState(false);
  const bottomRef = useRef(null);

  const adminId = localStorage.getItem("admin_id") || 1;
  const authUser = (() => { try { const u = JSON.parse(localStorage.getItem("adminuser") || "{}"); return u.name || "Assistant"; } catch { return "Assistant"; } })();

  const loadConversations = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const json = await apiFetch(`get_conversations.php?admin_id=${adminId}`);
      if (json.status) setConversations(json.data || []);
    } catch { setConversations([]); }
    if (!silent) setLoading(false);
  };

  const loadMessages = async (conv, silent = false) => {
    if (!silent) setSelected(conv);
    try {
      const json = await apiFetch(`get_thread.php?admin_id=${adminId}&with_id=${conv.user_id}&with_role=${conv.role || 'Patient'}`);
      if (json.status) setMessages(json.data || []);
    } catch { setMessages([]); }
  };

  // Initial Load
  useEffect(() => { 
    loadConversations(); 
    const interval = setInterval(() => loadConversations(true), 8000); // Poll convos every 8s
    return () => clearInterval(interval);
  }, []);

  // Poll thread if selected
  useEffect(() => {
    if (!selected) return;
    const interval = setInterval(() => loadMessages(selected, true), 4000); // Poll messages every 4s
    return () => clearInterval(interval);
  }, [selected]);

  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || !selected) return;
    const msg = newMsg.trim();
    setNewMsg("");
    setSending(true);
    
    // Optimistic update
    setMessages(prev => [...prev, { 
      sender: "You", 
      message: msg, 
      sent_at: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false }), 
      is_me: true 
    }]);

    try {
      await apiFetch("send_message.php", "POST", { 
        admin_id: adminId,
        to_id:    selected.user_id, 
        to_role:  selected.role || 'Patient',
        message:  msg 
      });
      // Immediately reload to confirm message is in DB
      loadMessages(selected, true);
      loadConversations(true);
    } catch {}
    setSending(false);
  };

  const filtered = conversations.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-3 p-lg-4" style={{ minHeight: "100vh", fontFamily: "'Nunito','Segoe UI',sans-serif" }}>
      <PageHeader title="Messages" subtitle="Communicate with doctors and patients" />

      <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: 16, height: "70vh" }}>
        <div className="row g-0 h-100">

          {/* Conversations list */}
          <div className="col-12 col-md-4 border-end d-flex flex-column" style={{ height: "100%" }}>
            <div className="p-3 border-bottom">
              <input className="form-control form-control-sm" placeholder="Search conversations..." value={search} onChange={e => setSearch(e.target.value)} style={{ fontSize: 13 }} />
            </div>
            <div className="flex-grow-1 overflow-auto">
              {loading ? <Loading /> : filtered.length === 0 ? <EmptyState message="No conversations." /> :
                filtered.map((conv, i) => (
                  <div key={i} onClick={() => loadMessages(conv)}
                    className="d-flex align-items-center gap-3 p-3 border-bottom"
                    style={{ cursor: "pointer", background: selected?.user_id === conv.user_id ? "#FAECE7" : "", borderLeft: selected?.user_id === conv.user_id ? "3px solid #D85A30" : "3px solid transparent" }}>
                    <Avatar name={conv.name} size={38} />
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="fw-semibold text-truncate" style={{ fontSize: 13 }}>{conv.name}</div>
                      <div className="text-muted text-truncate" style={{ fontSize: 11 }}>{conv.last_message || "No messages yet"}</div>
                    </div>
                    <div className="text-end flex-shrink-0">
                      <div className="text-muted" style={{ fontSize: 10 }}>{conv.last_time || ""}</div>
                      {conv.unread > 0 && <span className="badge" style={{ background: "#D85A30", fontSize: 10 }}>{conv.unread}</span>}
                    </div>
                  </div>
                ))
              }
            </div>
          </div>

          {/* Chat window */}
          <div className="col-12 col-md-8 d-flex flex-column" style={{ height: "100%" }}>
            {!selected ? (
              <div className="flex-grow-1 d-flex align-items-center justify-content-center">
                <div className="text-center text-muted">
                  <div style={{ fontSize: 36, marginBottom: 8 }}>💬</div>
                  <p className="mb-0" style={{ fontSize: 14 }}>Select a conversation to start messaging</p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat header */}
                <div className="px-4 py-3 border-bottom d-flex align-items-center gap-3" style={{ background: "#FAFBFC" }}>
                  <Avatar name={selected.name} size={36} />
                  <div>
                    <div className="fw-bold" style={{ fontSize: 14, color: "#0A1628" }}>{selected.name}</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>{selected.role || "User"}</div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-grow-1 overflow-auto p-3" style={{ background: "#F8FAFC" }}>
                  {messages.map((msg, i) => (
                    <div key={i} className={`d-flex mb-3 ${msg.is_me ? "justify-content-end" : "justify-content-start"}`}>
                      {!msg.is_me && <Avatar name={msg.sender} size={28} />}
                      <div className={`ms-2 me-2 px-3 py-2 rounded-3`} style={{
                        maxWidth: "70%",
                        background: msg.is_me ? "#D85A30" : "#fff",
                        color: msg.is_me ? "#fff" : "#0A1628",
                        fontSize: 13,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
                      }}>
                        <p className="mb-1">{msg.message}</p>
                        <small style={{ fontSize: 10, opacity: .7 }}>{msg.sent_at}</small>
                      </div>
                      {msg.is_me && <Avatar name={authUser} size={28} />}
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>

                {/* Input */}
                <div className="p-3 border-top d-flex gap-2" style={{ background: "#fff" }}>
                  <input
                    className="form-control"
                    placeholder="Type a message..."
                    value={newMsg}
                    onChange={e => setNewMsg(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
                    style={{ fontSize: 13, borderRadius: 10 }}
                  />
                  <button className="btn fw-bold text-white px-4" style={{ background: "#D85A30", borderRadius: 10 }} onClick={handleSend} disabled={sending || !newMsg.trim()}>
                    {sending ? <span className="spinner-border spinner-border-sm" /> : "Send"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}